import React, { useState, useEffect, useCallback } from 'react';
import { superAdmin, developers, apiKeys } from '../api';
import LoadingSpinner from './LoadingSpinner';
import DateRangePicker from './DateRangePicker';
import { 
  Building, Users, Mail, Plus, Edit2, Trash2, UserPlus, 
  CheckCircle2, XCircle, Clock, AlertCircle, RefreshCw,
  DollarSign, Bot, BarChart3, Calendar, TrendingUp, ArrowLeft,
  Database, Activity, AlertTriangle, Globe as GlobeIcon, Timer,
  PhoneIncoming, PhoneOutgoing, Briefcase, Key, Copy, Eye, EyeOff,
  Lock, Unlock, ShieldOff, ShieldCheck, KeyRound, User
} from 'lucide-react';
import ProfilePhoneSettings from './ProfilePhoneSettings';
import IPBlockingPanel from './IPBlockingPanel';
import { CHAT_IMAGE_LIMIT_FIELDS } from './chatImageAttachments';

const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('organizations');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Clients state
  const [clients, setClients] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientFormData, setClientFormData] = useState({
    name: '',
    email: '',
    company: '',
    org_ids: []
  });
  const [showAssignOrgModalClient, setShowAssignOrgModalClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedOrgIdsClient, setSelectedOrgIdsClient] = useState([]);

  // Organizations state
  const [organizations, setOrganizations] = useState([]);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgFormData, setOrgFormData] = useState({ name: '', max_minutes: '', chat_price_per_minute: '', elevenlabs_api_key: '', deepgram_api_key: '', cartesia_api_key: '', enforce_caller_whitelist: false, retention_days: '', chat_image_limits: {} });
  const [showOrgApiKeys, setShowOrgApiKeys] = useState(false);
  const [showOrgImageLimits, setShowOrgImageLimits] = useState(false);

  // Trial caller whitelist state (per org, managed in the org edit form)
  const [showTrialCallers, setShowTrialCallers] = useState(false);
  const [trialCallers, setTrialCallers] = useState([]);
  const [trialCallersLoading, setTrialCallersLoading] = useState(false);
  const [newTrialCaller, setNewTrialCaller] = useState({ caller_number: '', label: '', minutes_limit: '60' });

  // Developers state
  const [developersList, setDevelopersList] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    name: '',
    org_ids: []
  });
  const [showAssignOrgModal, setShowAssignOrgModal] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [showEditDeveloperModal, setShowEditDeveloperModal] = useState(false);
  const [editingDeveloper, setEditingDeveloper] = useState(null);
  const [developerEditData, setDeveloperEditData] = useState({
    is_super_admin: false,
    is_restricted: false
  });
  const [sendingResetDevId, setSendingResetDevId] = useState(null);

  // Invitations state
  const [invitations, setInvitations] = useState([]);

  // Usage state
  const [usageData, setUsageData] = useState(null);
  const [selectedOrgForUsage, setSelectedOrgForUsage] = useState(null);
  const [usagePeriod, setUsagePeriod] = useState('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);

  // Organization overview state
  const [selectedOrgOverview, setSelectedOrgOverview] = useState(null);
  const [orgOverviewData, setOrgOverviewData] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    type: null, // 'organization', 'developer', 'client', 'org-assignment', 'api-key'
    id: null,
    name: null,
    developerId: null, // For org-assignment type
    orgId: null // For org-assignment type
  });

  // API Keys state
  const [apiKeysList, setApiKeysList] = useState([]);
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [apiKeyFormData, setApiKeyFormData] = useState({
    key_name: '',
    scopes: null,
    expires_at: null
  });
  const [myProfile, setMyProfile] = useState(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'organizations') {
        const response = await superAdmin.listOrganizations();
        setOrganizations(response.data || []);
        // Set first org as default for usage if none selected
        if (!selectedOrgForUsage && response.data && response.data.length > 0) {
          setSelectedOrgForUsage(response.data[0].id);
        }
      } else if (activeTab === 'developers') {
        const response = await superAdmin.listDevelopers();
        setDevelopersList(response.data || []);
      } else if (activeTab === 'invitations') {
        const response = await superAdmin.listInvitations();
        setInvitations(response.data || []);
      } else if (activeTab === 'usage') {
        // Load organizations if not already loaded
        if (organizations.length === 0) {
          const orgsResponse = await superAdmin.listOrganizations();
          setOrganizations(orgsResponse.data || []);
          if (!selectedOrgForUsage && orgsResponse.data && orgsResponse.data.length > 0) {
            setSelectedOrgForUsage(orgsResponse.data[0].id);
          }
        }
        // Load usage data
        await fetchUsageData();
      } else if (activeTab === 'clients') {
        // Load organizations if not already loaded
        if (organizations.length === 0) {
          const orgsResponse = await superAdmin.listOrganizations();
          setOrganizations(orgsResponse.data || []);
        }
        const response = await superAdmin.listClients();
        setClients(response.data || []);
      } else if (activeTab === 'api-keys') {
        const response = await apiKeys.list();
        setApiKeysList(response.data || []);
      } else if (activeTab === 'profile') {
        const response = await developers.getMe();
        setMyProfile(response.data);
      }
    } catch (err) {
      console.error('SuperAdmin API Error:', err);
      let errorMessage = 'Failed to load data';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      // Set empty arrays on error to prevent rendering issues
      if (activeTab === 'organizations') {
        setOrganizations([]);
      } else if (activeTab === 'developers') {
        setDevelopersList([]);
      } else if (activeTab === 'invitations') {
        setInvitations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = useCallback(async () => {
    if (!selectedOrgForUsage) {
      setUsageData(null);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = useCustomRange && customStartDate && customEndDate
        ? await superAdmin.getUsage(selectedOrgForUsage, null, customStartDate, customEndDate)
        : await superAdmin.getUsage(selectedOrgForUsage, usagePeriod);
      setUsageData(response.data);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage data');
      setUsageData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgForUsage, usagePeriod, customStartDate, customEndDate, useCustomRange]);

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      // Prepare form data - convert empty numeric fields to null, keep vendor keys as-is
      const submitData = {
        name: orgFormData.name,
        max_minutes: orgFormData.max_minutes === '' || orgFormData.max_minutes === null
          ? null
          : parseFloat(orgFormData.max_minutes),
        chat_price_per_minute: orgFormData.chat_price_per_minute === '' || orgFormData.chat_price_per_minute === null
          ? null
          : parseFloat(orgFormData.chat_price_per_minute),
        elevenlabs_api_key: orgFormData.elevenlabs_api_key || null,
        deepgram_api_key: orgFormData.deepgram_api_key || null,
        cartesia_api_key: orgFormData.cartesia_api_key || null,
        enforce_caller_whitelist: !!orgFormData.enforce_caller_whitelist,
        retention_days: orgFormData.retention_days === '' || orgFormData.retention_days === null
          ? null
          : parseInt(orgFormData.retention_days, 10),
        // Blank fields are left out, so they fall back to the defaults.
        chat_image_limits: Object.fromEntries(
          Object.entries(orgFormData.chat_image_limits || {})
            .filter(([, value]) => value !== '' && value !== null && value !== undefined)
            .map(([key, value]) => [key, parseInt(value, 10)])
        ),
      };

      if (editingOrg) {
        await superAdmin.updateOrganization(editingOrg.id, submitData);
        setSuccessMessage('Organization updated successfully');
      } else {
        await superAdmin.createOrganization(submitData);
        setSuccessMessage('Organization created successfully');
      }
      await loadData();
      resetOrgForm();
    } catch (err) {
      // Handle validation errors properly
      let errorMessage = 'Failed to save organization';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } 
        // Handle single error detail
        else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' 
            ? errorData.detail 
            : JSON.stringify(errorData.detail);
        }
        // Handle error message directly
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const openDeleteModal = (type, id, name, developerId = null, orgId = null) => {
    setDeleteConfirmation({ type, id, name, developerId, orgId });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmation({ type: null, id: null, name: null, developerId: null, orgId: null });
  };

  const confirmDelete = async () => {
    const { type, id, name, developerId, orgId } = deleteConfirmation;
    
    try {
      if (type === 'organization') {
        const { data } = await superAdmin.deleteOrganization(id);
        // Numbers on a provider with no automated release path have to be
        // cancelled by hand, so name them instead of reporting a clean delete.
        const manual = data?.manual_release_required || [];
        setSuccessMessage(
          manual.length
            ? `Organization deleted. Release these numbers manually with the provider: ${manual.join(', ')}`
            : 'Organization deleted successfully'
        );
      } else if (type === 'developer') {
        await superAdmin.deleteDeveloper(id);
        setSuccessMessage('Developer deleted successfully');
      } else if (type === 'client') {
        await superAdmin.deleteClient(id);
        setSuccessMessage('Client deleted successfully');
      } else if (type === 'org-assignment') {
        await superAdmin.removeOrganization(developerId, orgId);
        setSuccessMessage('Organization assignment removed');
      } else if (type === 'api-key') {
        await apiKeys.delete(id);
        setSuccessMessage('API key deleted successfully');
      } else if (type === 'revoke-access') {
        await superAdmin.toggleDeveloperAccess(id, false);
        setSuccessMessage(`Login access revoked for "${name}"`);
      } else if (type === 'restore-access') {
        await superAdmin.toggleDeveloperAccess(id, true);
        setSuccessMessage(`Login access restored for "${name}"`);
      }
      
      await loadData();
      closeDeleteModal();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${type}`);
    }
  };

  const handleDeleteOrg = (orgId, orgName) => {
    openDeleteModal('organization', orgId, orgName);
  };

  // API Keys handlers
  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await apiKeys.create(apiKeyFormData);
      setNewlyCreatedKey(response.data);
      setShowKeyModal(true);
      setShowApiKeyForm(false);
      setApiKeyFormData({ key_name: '', scopes: null, expires_at: null });
      await loadData();
      setSuccessMessage('API key created successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    try {
      await apiKeys.revoke(keyId);
      setSuccessMessage('API key revoked successfully');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const handleDeleteApiKey = (keyId, keyName) => {
    openDeleteModal('api-key', keyId, keyName);
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await superAdmin.inviteDeveloper(inviteFormData);
      setSuccessMessage(
        response.data.email_sent 
          ? 'Invitation sent successfully via email'
          : 'Invitation created. Please share the token manually: ' + response.data.token
      );
      await loadData();
      resetInviteForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleSendPasswordReset = async (dev) => {
    setError(null);
    setSuccessMessage(null);
    setSendingResetDevId(dev.id);

    try {
      const response = await superAdmin.sendPasswordReset(dev.id);
      if (response.data.email_sent) {
        setSuccessMessage(`Password reset email sent to ${dev.email}`);
      } else {
        setSuccessMessage(
          `Reset link created for ${dev.email}. Share manually: ${response.data.reset_url}`
        );
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setSendingResetDevId(null);
    }
  };

  const handleAssignOrg = async (developerId, orgIds) => {
    try {
      // Update all organization assignments at once
      await superAdmin.updateDeveloperOrganizations(developerId, { org_ids: orgIds });
      setSuccessMessage('Organization assignments updated successfully');
      await loadData();
      setShowAssignOrgModal(false);
      setSelectedDeveloper(null);
      setSelectedOrgIds([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update organization assignments');
    }
  };

  const openAssignOrgModal = (developer) => {
    setSelectedDeveloper(developer);
    // Pre-select currently assigned organizations
    const currentOrgIds = developer.organizations.map(org => org.id);
    setSelectedOrgIds(currentOrgIds);
    setShowAssignOrgModal(true);
  };

  const handleOrgSelection = (orgId) => {
    setSelectedOrgIds(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  };

  const handleRemoveOrg = (developerId, orgId, orgName) => {
    openDeleteModal('org-assignment', null, orgName, developerId, orgId);
  };

  const openEditDeveloperModal = (developer) => {
    setEditingDeveloper(developer);
    setDeveloperEditData({
      is_super_admin: developer.is_super_admin || false,
      is_restricted: developer.is_restricted || false
    });
    setShowEditDeveloperModal(true);
  };

  const handleEditDeveloperSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      await superAdmin.updateDeveloper(editingDeveloper.id, developerEditData);
      setSuccessMessage('Developer updated successfully');
      await loadData();
      resetEditDeveloperForm();
    } catch (err) {
      let errorMessage = 'Failed to update developer';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        }
      }
      setError(errorMessage);
    }
  };

  const resetEditDeveloperForm = () => {
    setShowEditDeveloperModal(false);
    setEditingDeveloper(null);
    setDeveloperEditData({
      is_super_admin: false,
      is_restricted: false
    });
  };

  const handleDeleteDeveloper = (developerId, developerName) => {
    openDeleteModal('developer', developerId, developerName);
  };

  const handleToggleAccess = (developer) => {
    const type = developer.is_active ? 'revoke-access' : 'restore-access';
    openDeleteModal(type, developer.id, developer.name);
  };

  const resetOrgForm = () => {
    setOrgFormData({ name: '', max_minutes: '', chat_price_per_minute: '', elevenlabs_api_key: '', deepgram_api_key: '', cartesia_api_key: '', enforce_caller_whitelist: false, retention_days: '', chat_image_limits: {} });
    setShowOrgForm(false);
    setShowOrgApiKeys(false);
    setShowTrialCallers(false);
    setTrialCallers([]);
    setNewTrialCaller({ caller_number: '', label: '', minutes_limit: '60' });
    setEditingOrg(null);
  };

  const loadTrialCallers = async (orgId) => {
    if (!orgId) return;
    setTrialCallersLoading(true);
    try {
      const res = await superAdmin.listTrialCallers(orgId);
      setTrialCallers(res.data.callers || []);
    } catch (err) {
      setError('Failed to load trial callers');
    } finally {
      setTrialCallersLoading(false);
    }
  };

  const handleAddTrialCaller = async () => {
    if (!editingOrg) return;
    if (!newTrialCaller.caller_number.trim()) {
      setError('Enter a caller number');
      return;
    }
    try {
      await superAdmin.addTrialCaller(editingOrg.id, {
        caller_number: newTrialCaller.caller_number.trim(),
        label: newTrialCaller.label.trim() || null,
        minutes_limit: newTrialCaller.minutes_limit === '' ? null : parseFloat(newTrialCaller.minutes_limit),
      });
      setNewTrialCaller({ caller_number: '', label: '', minutes_limit: '60' });
      setSuccessMessage('Caller added to whitelist');
      await loadTrialCallers(editingOrg.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add caller');
    }
  };

  const handleToggleTrialCallerActive = async (caller) => {
    if (!editingOrg) return;
    try {
      await superAdmin.updateTrialCaller(editingOrg.id, caller.id, { is_active: !caller.is_active });
      await loadTrialCallers(editingOrg.id);
    } catch (err) {
      setError('Failed to update caller');
    }
  };

  const handleDeleteTrialCaller = async (caller) => {
    if (!editingOrg) return;
    if (!window.confirm(`Remove ${caller.caller_number} from the whitelist?`)) return;
    try {
      await superAdmin.deleteTrialCaller(editingOrg.id, caller.id);
      setSuccessMessage('Caller removed');
      await loadTrialCallers(editingOrg.id);
    } catch (err) {
      setError('Failed to remove caller');
    }
  };

  const resetInviteForm = () => {
    setInviteFormData({ email: '', name: '', org_ids: [] });
    setShowInviteForm(false);
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      if (editingClient) {
        await superAdmin.updateClient(editingClient.id, clientFormData);
        setSuccessMessage('Client updated successfully');
      } else {
        await superAdmin.createClient(clientFormData);
        setSuccessMessage('Client created successfully');
      }
      await loadData();
      resetClientForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDeleteClient = (clientId, clientName) => {
    openDeleteModal('client', clientId, clientName);
  };

  const handleAssignOrgClient = async (clientId, orgIds) => {
    try {
      await superAdmin.updateClientOrganizations(clientId, { org_ids: orgIds });
      setSuccessMessage('Organization assignments updated successfully');
      await loadData();
      setShowAssignOrgModalClient(false);
      setSelectedClient(null);
      setSelectedOrgIdsClient([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update organization assignments');
    }
  };

  const openAssignOrgModalClient = (client) => {
    setSelectedClient(client);
    const currentOrgIds = client.organizations.map(org => org.id);
    setSelectedOrgIdsClient(currentOrgIds);
    setShowAssignOrgModalClient(true);
  };

  const handleOrgSelectionClient = (orgId) => {
    setSelectedOrgIdsClient(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  };

  const resetClientForm = () => {
    setClientFormData({ name: '', email: '', company: '', org_ids: [] });
    setShowClientForm(false);
    setEditingClient(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
      accepted: { icon: CheckCircle2, color: 'bg-green-500/10 text-green-500 border-green-500/20' },
      expired: { icon: XCircle, color: 'bg-red-500/10 text-red-500 border-red-500/20' },
      revoked: { icon: XCircle, color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Super Admin Dashboard
            </h1>
            <p className="text-muted-foreground">Manage organizations, developers, and invitations</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border/50 pb-2">
          {[
            { id: 'organizations', label: 'Organizations', icon: Building },
            { id: 'developers', label: 'Developers', icon: Users },
            { id: 'clients', label: 'Clients', icon: Briefcase },
            { id: 'invitations', label: 'Invitations', icon: Mail },
            { id: 'usage', label: 'Usage', icon: BarChart3 },
            { id: 'api-keys', label: 'API Keys', icon: Key },
            { id: 'ip-blocking', label: 'IP Based Blocking', icon: ShieldOff },
            { id: 'profile', label: 'Profile', icon: User },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all duration-300 rounded-t-lg ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 backdrop-blur-sm border border-destructive/20 text-destructive rounded-xl flex items-start gap-3 shadow-lg shadow-destructive/5 animate-slide-in-up">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="font-semibold">Error:</strong> {typeof error === 'string' ? error : JSON.stringify(error)}
            </div>
          </div>
        )}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 backdrop-blur-sm border border-green-500/20 text-green-500 rounded-xl flex items-start gap-3 shadow-lg shadow-green-500/5 animate-slide-in-up">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="font-medium">{successMessage}</div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            {/* Organizations Tab */}
            {activeTab === 'organizations' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Organizations</h2>
                    <p className="text-sm text-muted-foreground mt-1">Manage and view organization details</p>
                  </div>
                  <button
                    onClick={() => setShowOrgForm(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                  >
                    <Plus className="w-5 h-5" />
                    Create Organization
                  </button>
                </div>

                {showOrgForm && (
                  <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5 animate-slide-in-up">
                    <form onSubmit={handleOrgSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Organization Name</label>
                        <input
                          type="text"
                          value={orgFormData.name}
                          onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                          required
                          className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          placeholder="Enter organization name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">
                          Maximum Minutes Limit (Optional)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={orgFormData.max_minutes}
                          onChange={(e) => setOrgFormData({ ...orgFormData, max_minutes: e.target.value })}
                          className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          placeholder="Leave empty for unlimited (e.g., 1000)"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Set a maximum minutes limit for this organization. Calls will be blocked once the limit is reached. Leave empty for unlimited usage.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">
                          Chat Price Per Minute (USD) — Embed Chat Billing
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">$</span>
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={orgFormData.chat_price_per_minute}
                            onChange={(e) => setOrgFormData({ ...orgFormData, chat_price_per_minute: e.target.value })}
                            className="w-full pl-7 pr-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                            placeholder="e.g. 0.05 (leave empty to disable)"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Per-minute rate charged for embed chat sessions. Cost = price × duration in minutes. Leave empty to disable chat billing (falls back to LLM token cost).
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">
                          Data Retention (Days) — Recordings &amp; Transcripts
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={orgFormData.retention_days}
                          onChange={(e) => setOrgFormData({ ...orgFormData, retention_days: e.target.value })}
                          className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          placeholder="Leave empty to retain forever (e.g., 90)"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Call recordings and transcripts older than this are permanently deleted from S3 and the database on the weekly cleanup run. Leave empty to retain indefinitely.
                        </p>
                      </div>

                      {/* Chat Image Limits */}
                      <div className="border border-border/40 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowOrgImageLimits(!showOrgImageLimits)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-medium text-foreground"
                        >
                          <span>
                            Chat Image Limits (Optional)
                            {Object.values(orgFormData.chat_image_limits || {}).some((v) => v !== '' && v != null) && (
                              <span className="ml-2 text-xs text-amber-400">custom</span>
                            )}
                          </span>
                          <span className="text-muted-foreground text-xs">{showOrgImageLimits ? '▲ Hide' : '▼ Show'}</span>
                        </button>
                        {showOrgImageLimits && (
                          <div className="px-4 py-4 space-y-4 bg-background/30">
                            <p className="text-xs text-muted-foreground">
                              Limits for images visitors attach in chat, Combo Widget and Avatar Panel conversations, for every agent in this organization. Leave a field empty to use the default. Per-minute and per-day limits count uploads per visitor IP, per agent.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {CHAT_IMAGE_LIMIT_FIELDS.map((field) => (
                                <div key={field.key}>
                                  <label className="block text-xs font-medium mb-1.5 text-foreground">{field.label}</label>
                                  <input
                                    type="number"
                                    step="1"
                                    min={field.min}
                                    max={field.max}
                                    value={orgFormData.chat_image_limits?.[field.key] ?? ''}
                                    onChange={(e) => setOrgFormData({
                                      ...orgFormData,
                                      chat_image_limits: { ...(orgFormData.chat_image_limits || {}), [field.key]: e.target.value },
                                    })}
                                    className="w-full px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                                    placeholder={`Default: ${field.fallback}`}
                                  />
                                  <p className="text-[11px] text-muted-foreground mt-1">Allowed {field.min}–{field.max}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Raising "Images the AI sees per turn" raises cost on every reply, since those images are sent to the model again each turn.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Vendor API Keys Section */}
                      <div className="border border-border/40 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowOrgApiKeys(!showOrgApiKeys)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-medium text-foreground"
                        >
                          <span>Custom Vendor API Keys (Optional)</span>
                          <span className="text-muted-foreground text-xs">{showOrgApiKeys ? '▲ Hide' : '▼ Show'}</span>
                        </button>
                        {showOrgApiKeys && (
                          <div className="px-4 py-4 space-y-4 bg-background/30">
                            <p className="text-xs text-muted-foreground">
                              Override the server-default API keys for this organization. Leave blank to use the server defaults. Changes take effect on the next call.
                            </p>

                            {/* ElevenLabs */}
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs font-medium text-foreground">ElevenLabs API Key</label>
                                {editingOrg?.has_elevenlabs_key && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!window.confirm('Reset ElevenLabs key to server default?')) return;
                                      try {
                                        await superAdmin.clearOrgVendorKey(editingOrg.id, 'elevenlabs');
                                        await loadData();
                                        setEditingOrg(prev => ({ ...prev, has_elevenlabs_key: false }));
                                        setOrgFormData(prev => ({ ...prev, elevenlabs_api_key: '' }));
                                        setSuccessMessage('ElevenLabs key cleared — org will use server default');
                                      } catch (err) {
                                        setError('Failed to clear ElevenLabs key');
                                      }
                                    }}
                                    className="text-xs text-destructive hover:text-destructive/80 hover:underline transition-colors"
                                  >
                                    ✕ Reset to server default
                                  </button>
                                )}
                              </div>
                              <input
                                type="password"
                                value={orgFormData.elevenlabs_api_key}
                                onChange={(e) => setOrgFormData({ ...orgFormData, elevenlabs_api_key: e.target.value })}
                                className="w-full px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 font-mono"
                                placeholder={editingOrg?.has_elevenlabs_key ? '(key set — enter new key to replace)' : 'Leave blank to use server default'}
                                autoComplete="new-password"
                              />
                              {editingOrg?.has_elevenlabs_key && (
                                <p className="text-xs text-amber-500 mt-1">⚠ Custom key active. Enter a new key to replace, or click "Reset to server default" to remove it.</p>
                              )}
                            </div>

                            {/* Deepgram */}
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs font-medium text-foreground">Deepgram API Key</label>
                                {editingOrg?.has_deepgram_key && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!window.confirm('Reset Deepgram key to server default?')) return;
                                      try {
                                        await superAdmin.clearOrgVendorKey(editingOrg.id, 'deepgram');
                                        await loadData();
                                        setEditingOrg(prev => ({ ...prev, has_deepgram_key: false }));
                                        setOrgFormData(prev => ({ ...prev, deepgram_api_key: '' }));
                                        setSuccessMessage('Deepgram key cleared — org will use server default');
                                      } catch (err) {
                                        setError('Failed to clear Deepgram key');
                                      }
                                    }}
                                    className="text-xs text-destructive hover:text-destructive/80 hover:underline transition-colors"
                                  >
                                    ✕ Reset to server default
                                  </button>
                                )}
                              </div>
                              <input
                                type="password"
                                value={orgFormData.deepgram_api_key}
                                onChange={(e) => setOrgFormData({ ...orgFormData, deepgram_api_key: e.target.value })}
                                className="w-full px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 font-mono"
                                placeholder={editingOrg?.has_deepgram_key ? '(key set — enter new key to replace)' : 'Leave blank to use server default'}
                                autoComplete="new-password"
                              />
                              {editingOrg?.has_deepgram_key && (
                                <p className="text-xs text-amber-500 mt-1">⚠ Custom key active. Enter a new key to replace, or click "Reset to server default" to remove it.</p>
                              )}
                            </div>

                            {/* Cartesia */}
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs font-medium text-foreground">Cartesia API Key</label>
                                {editingOrg?.has_cartesia_key && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!window.confirm('Reset Cartesia key to server default?')) return;
                                      try {
                                        await superAdmin.clearOrgVendorKey(editingOrg.id, 'cartesia');
                                        await loadData();
                                        setEditingOrg(prev => ({ ...prev, has_cartesia_key: false }));
                                        setOrgFormData(prev => ({ ...prev, cartesia_api_key: '' }));
                                        setSuccessMessage('Cartesia key cleared — org will use server default');
                                      } catch (err) {
                                        setError('Failed to clear Cartesia key');
                                      }
                                    }}
                                    className="text-xs text-destructive hover:text-destructive/80 hover:underline transition-colors"
                                  >
                                    ✕ Reset to server default
                                  </button>
                                )}
                              </div>
                              <input
                                type="password"
                                value={orgFormData.cartesia_api_key}
                                onChange={(e) => setOrgFormData({ ...orgFormData, cartesia_api_key: e.target.value })}
                                className="w-full px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 font-mono"
                                placeholder={editingOrg?.has_cartesia_key ? '(key set — enter new key to replace)' : 'Leave blank to use server default'}
                                autoComplete="new-password"
                              />
                              {editingOrg?.has_cartesia_key && (
                                <p className="text-xs text-amber-500 mt-1">⚠ Custom key active. Enter a new key to replace, or click "Reset to server default" to remove it.</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Caller Whitelist (Trial) Section */}
                      <div className="border border-border/40 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-muted/30">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!orgFormData.enforce_caller_whitelist}
                              onChange={(e) => setOrgFormData({ ...orgFormData, enforce_caller_whitelist: e.target.checked })}
                              className="mt-1 w-4 h-4 accent-primary"
                            />
                            <span>
                              <span className="block text-sm font-medium text-foreground">Enforce caller-ID whitelist (Trial)</span>
                              <span className="block text-xs text-muted-foreground mt-0.5">
                                Off by default. When on, only the whitelisted numbers below may call this org's agents, each capped by its own minute limit. Leave off for normal organizations.
                              </span>
                            </span>
                          </label>
                        </div>

                        {orgFormData.enforce_caller_whitelist && (
                          <div className="px-4 py-4 bg-background/30 border-t border-border/40">
                            {!editingOrg ? (
                              <p className="text-xs text-muted-foreground">
                                Save the organization first, then re-open it to add whitelisted numbers.
                              </p>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = !showTrialCallers;
                                    setShowTrialCallers(next);
                                    if (next) loadTrialCallers(editingOrg.id);
                                  }}
                                  className="text-sm font-medium text-primary hover:underline"
                                >
                                  {showTrialCallers ? '▲ Hide whitelisted numbers' : '▼ Manage whitelisted numbers'}
                                </button>

                                {showTrialCallers && (
                                  <div className="mt-4 space-y-4">
                                    {/* Add new caller */}
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                                      <input
                                        type="text"
                                        value={newTrialCaller.caller_number}
                                        onChange={(e) => setNewTrialCaller({ ...newTrialCaller, caller_number: e.target.value })}
                                        placeholder="+14155551234"
                                        className="sm:col-span-4 px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
                                      />
                                      <input
                                        type="text"
                                        value={newTrialCaller.label}
                                        onChange={(e) => setNewTrialCaller({ ...newTrialCaller, label: e.target.value })}
                                        placeholder="label (e.g. user id / email)"
                                        className="sm:col-span-4 px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                      />
                                      <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={newTrialCaller.minutes_limit}
                                        onChange={(e) => setNewTrialCaller({ ...newTrialCaller, minutes_limit: e.target.value })}
                                        placeholder="min (60)"
                                        className="sm:col-span-2 px-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                      />
                                      <button
                                        type="button"
                                        onClick={handleAddTrialCaller}
                                        className="sm:col-span-2 px-3 py-2 bg-gradient-button text-primary-foreground rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                                      >
                                        Add
                                      </button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Minute limit is per caller. Leave blank for unlimited.</p>

                                    {/* Existing callers */}
                                    {trialCallersLoading ? (
                                      <p className="text-xs text-muted-foreground">Loading…</p>
                                    ) : trialCallers.length === 0 ? (
                                      <p className="text-xs text-muted-foreground">No numbers whitelisted yet.</p>
                                    ) : (
                                      <div className="space-y-2">
                                        {trialCallers.map((c) => (
                                          <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-background/50 border border-border/30 rounded-lg">
                                            <div className="min-w-0">
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-mono text-foreground">{c.caller_number}</span>
                                                {!c.is_active && (
                                                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">paused</span>
                                                )}
                                              </div>
                                              <div className="text-xs text-muted-foreground truncate">
                                                {c.label ? `${c.label} · ` : ''}
                                                {c.minutes_used} min used{c.minutes_limit != null ? ` / ${c.minutes_limit} (${c.minutes_remaining} left)` : ' (unlimited)'}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                              <button
                                                type="button"
                                                onClick={() => handleToggleTrialCallerActive(c)}
                                                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                                              >
                                                {c.is_active ? 'Pause' : 'Resume'}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleDeleteTrialCaller(c)}
                                                className="text-xs text-destructive hover:text-destructive/80 hover:underline"
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3">
                        <button 
                          type="submit" 
                          className="px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                        >
                          {editingOrg ? 'Update' : 'Create'}
                        </button>
                        <button 
                          type="button" 
                          onClick={resetOrgForm} 
                          className="px-6 py-3 bg-muted/50 text-foreground rounded-lg hover:bg-muted transition-all duration-300 font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {selectedOrgOverview ? (
                  <div className="space-y-6">
                    <button
                      onClick={() => {
                        setSelectedOrgOverview(null);
                        setOrgOverviewData(null);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-all duration-300 group"
                    >
                      <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                      <span className="font-medium">Back to Organizations</span>
                    </button>
                    {loadingOverview ? (
                      <div className="flex justify-center py-12">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : orgOverviewData ? (
                      <div className="space-y-6">
                        {/* Overview Stats */}
                        <div>
                          <h2 className="text-2xl font-bold mb-1">Overview</h2>
                          <p className="text-sm text-muted-foreground mb-4">Dashboard overview and analytics</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                            {/* Total Agents */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <Bot className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-foreground">
                                    {orgOverviewData.stats.total_agents}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Total Agents</div>
                                </div>
                              </div>
                            </div>

                            {/* Total Sessions */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <Database className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-foreground">
                                    {orgOverviewData.stats.total_sessions}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Total Sessions</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Active Sessions */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <Activity className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-success">
                                    {orgOverviewData.stats.active_sessions}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Active Now</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Abandoned Sessions */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <AlertTriangle className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-warning">
                                    {orgOverviewData.stats.abandoned_sessions}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Abandoned</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Web Sessions */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <GlobeIcon className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-primary">
                                    {orgOverviewData.stats.web_sessions}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Web Sessions</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mt-3">
                            {/* Avg Duration */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <Timer className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-secondary-cyan">
                                    {orgOverviewData.stats.avg_duration}s
                                  </div>
                                  <div className="text-xs text-muted-foreground">Avg Duration</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Total Cost */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <DollarSign className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-success">
                                    ${orgOverviewData.stats.total_cost_usd.toFixed(4)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Total Cost</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Avg Cost */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <TrendingUp className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-primary">
                                    ${orgOverviewData.stats.avg_cost_usd.toFixed(4)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Avg Cost</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Total Minutes */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <Clock className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-secondary-cyan">
                                    {(orgOverviewData.stats.total_minutes || 0).toFixed(2)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Total Minutes</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Inbound Phone Sessions */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <PhoneIncoming className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-success">
                                    {orgOverviewData.stats.inbound_phone_sessions || 0}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Inbound Calls</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Outbound Phone Sessions */}
                            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                  <PhoneOutgoing className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xl font-bold text-primary">
                                    {orgOverviewData.stats.outbound_phone_sessions || 0}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Outbound Calls</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Multi-Column Layout: Agent Usage & Developers */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Agent Usage Table - Takes 2 columns */}
                          <div className="lg:col-span-2">
                            <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl overflow-hidden shadow-lg shadow-primary/5 h-full">
                              <div className="p-4 border-b border-border/50 bg-gradient-to-r from-primary/5 to-transparent">
                                <div className="flex items-center gap-2">
                                  <BarChart3 className="w-5 h-5 text-primary" />
                                  <h2 className="text-lg font-semibold text-foreground">Agent Usage</h2>
                                </div>
                              </div>
                              
                              {orgOverviewData.agent_usage.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                  <Bot className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                  <p>No agent usage data available</p>
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="border-b border-border/50 bg-muted/30">
                                        <th className="text-left p-4 text-sm font-medium text-foreground">Agent</th>
                                        <th className="text-right p-4 text-sm font-medium text-foreground">Minutes</th>
                                        <th className="text-right p-4 text-sm font-medium text-foreground">Cost</th>
                                        <th className="text-right p-4 text-sm font-medium text-foreground">Sessions</th>
                                        <th className="text-right p-4 text-sm font-medium text-foreground">Avg Cost/Min</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {orgOverviewData.agent_usage.map((agent, index) => {
                                        const avgCostPerMin = agent.total_minutes > 0 
                                          ? agent.total_cost_usd / agent.total_minutes 
                                          : 0;
                                        
                                        return (
                                          <tr
                                            key={agent.agent_id}
                                            className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${
                                              index % 2 === 0 ? 'bg-card/30' : ''
                                            }`}
                                          >
                                            <td className="p-4">
                                              <div className="flex items-center gap-2">
                                                <Bot className="w-4 h-4 text-primary" />
                                                <span className="font-medium text-foreground">{agent.agent_name}</span>
                                              </div>
                                            </td>
                                            <td className="p-4 text-right">
                                              <div className="flex items-center justify-end gap-2">
                                                <Clock className="w-4 h-4 text-muted-foreground" />
                                                <span className="text-foreground font-medium">
                                                  {agent.total_minutes.toFixed(2)}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="p-4 text-right">
                                              <div className="flex items-center justify-end gap-2">
                                                <DollarSign className="w-4 h-4 text-muted-foreground" />
                                                <span className="text-foreground font-medium">
                                                  ${agent.total_cost_usd.toFixed(4)}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="p-4 text-right">
                                              <span className="text-foreground">{agent.session_count}</span>
                                            </td>
                                            <td className="p-4 text-right">
                                              <div className="flex items-center justify-end gap-2">
                                                <TrendingUp className="w-4 h-4 text-muted-foreground" />
                                                <span className="text-foreground">
                                                  ${avgCostPerMin.toFixed(4)}
                                                </span>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Developers List - Takes 1 column */}
                          <div className="lg:col-span-1">
                            <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl overflow-hidden shadow-lg shadow-primary/5 h-full">
                              <div className="p-4 border-b border-border/50 bg-gradient-to-r from-primary/5 to-transparent">
                                <div className="flex items-center gap-2">
                                  <Users className="w-5 h-5 text-primary" />
                                  <h2 className="text-lg font-semibold text-foreground">Developers</h2>
                                </div>
                              </div>
                              
                              {orgOverviewData.developers.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                  <p className="text-sm">No developers associated</p>
                                </div>
                              ) : (
                                <div className="divide-y divide-border/50 max-h-[600px] overflow-y-auto">
                                  {orgOverviewData.developers.map((dev) => (
                                    <div key={dev.id} className="p-4 hover:bg-muted/20 transition-colors group">
                                      <div className="space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-foreground truncate">{dev.name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{dev.email}</div>
                                          </div>
                                          {dev.is_super_admin && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-purple-500/10 text-purple-500 border border-purple-500/20 flex-shrink-0">
                                              Super Admin
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {dev.last_login_at 
                                            ? `Last login: ${new Date(dev.last_login_at).toLocaleDateString()}`
                                            : 'Never logged in'}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {organizations.length === 0 ? (
                      <div className="col-span-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center shadow-lg shadow-primary/5">
                        <Building className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground text-lg">No organizations found. Create one to get started.</p>
                      </div>
                    ) : (
                      organizations.map(org => (
                      <div 
                        key={org.id} 
                        className="group relative bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 cursor-pointer overflow-hidden"
                        onClick={async () => {
                          setSelectedOrgOverview(org.id);
                          setLoadingOverview(true);
                          try {
                            const response = await superAdmin.getOrganizationOverview(org.id);
                            setOrgOverviewData(response.data);
                          } catch (err) {
                            setError(err.response?.data?.detail || 'Failed to load organization overview');
                            setSelectedOrgOverview(null);
                          } finally {
                            setLoadingOverview(false);
                          }
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                                <Building className="w-6 h-6 text-primary-foreground" />
                              </div>
                              <div>
                                <h3 className="font-bold text-lg text-foreground">{org.name}</h3>
                                <p className="text-xs text-muted-foreground">
                                  Created {new Date(org.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingOrg(org);
                                  setOrgFormData({ name: org.name, max_minutes: org.max_minutes || '', chat_price_per_minute: org.chat_price_per_minute != null ? org.chat_price_per_minute : '', elevenlabs_api_key: '', deepgram_api_key: '', cartesia_api_key: '', enforce_caller_whitelist: !!org.enforce_caller_whitelist, retention_days: org.retention_days != null ? org.retention_days : '', chat_image_limits: { ...(org.chat_image_limits || {}) } });
                                  setShowTrialCallers(false);
                                  setTrialCallers([]);
                                  setShowOrgForm(true);
                                }}
                                className="p-2 hover:bg-muted/50 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOrg(org.id, org.name);
                                }}
                                className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mt-4">
                            <div className="bg-background/50 rounded-lg p-3 border border-border/30">
                              <div className="text-xs text-muted-foreground mb-1">Agents</div>
                              <div className="text-lg font-bold text-foreground">{org.agent_count}</div>
                            </div>
                            <div className="bg-background/50 rounded-lg p-3 border border-border/30">
                              <div className="text-xs text-muted-foreground mb-1">Sessions</div>
                              <div className="text-lg font-bold text-foreground">{org.session_count}</div>
                            </div>
                          </div>
                          {/* Chat pricing badge */}
                          {org.chat_price_per_minute != null && (
                            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                              <span className="text-xs font-semibold text-emerald-500">💬 Chat billing</span>
                              <span className="text-xs text-emerald-400">${Number(org.chat_price_per_minute).toFixed(4)} / min</span>
                            </div>
                          )}
                          {org.retention_days != null && org.retention_days > 0 && (
                            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-500/10 border border-sky-500/20">
                              <span className="text-xs font-semibold text-sky-500">🗑 Retention</span>
                              <span className="text-xs text-sky-400">{org.retention_days} days</span>
                            </div>
                          )}
                          {org.chat_image_limits && Object.keys(org.chat_image_limits).length > 0 && (
                            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
                              <span className="text-xs font-semibold text-violet-500">🖼 Image limits</span>
                              <span className="text-xs text-violet-400">
                                {CHAT_IMAGE_LIMIT_FIELDS.filter((f) => org.chat_image_limits[f.key] != null)
                                  .map((f) => `${f.label.split(' (')[0]}: ${org.chat_image_limits[f.key]}`)
                                  .join(' · ')}
                              </span>
                            </div>
                          )}
                          {org.max_minutes != null && (
                            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                              <span className="text-xs font-semibold text-amber-500">⏱ Minutes cap</span>
                              <span className="text-xs text-amber-400">{org.max_minutes} min</span>
                            </div>
                          )}
                          {(org.has_elevenlabs_key || org.has_deepgram_key || org.has_cartesia_key) && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              {org.has_elevenlabs_key && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-600 border border-amber-500/30 font-medium">
                                  🔑 ElevenLabs
                                </span>
                              )}
                              {org.has_deepgram_key && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-500/15 text-blue-600 border border-blue-500/30 font-medium">
                                  🔑 Deepgram
                                </span>
                              )}
                              {org.has_cartesia_key && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/15 text-purple-600 border border-purple-500/30 font-medium">
                                  🔑 Cartesia
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Developers Tab */}
            {activeTab === 'developers' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Developers</h2>
                    <p className="text-sm text-muted-foreground mt-1">Manage developer accounts and permissions</p>
                  </div>
                  <button
                    onClick={() => setShowInviteForm(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                  >
                    <UserPlus className="w-5 h-5" />
                    Invite Developer
                  </button>
                </div>

                {showInviteForm && (
                  <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5 animate-slide-in-up">
                    <form onSubmit={handleInviteSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
                        <input
                          type="email"
                          value={inviteFormData.email}
                          onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                          required
                          className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          placeholder="developer@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Name</label>
                        <input
                          type="text"
                          value={inviteFormData.name}
                          onChange={(e) => setInviteFormData({ ...inviteFormData, name: e.target.value })}
                          required
                          className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          placeholder="John Developer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Assign Organizations (Optional)</label>
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-border/50 rounded-lg p-3 bg-background/50 backdrop-blur-sm">
                          {organizations.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No organizations available</p>
                          ) : (
                            organizations.map(org => (
                              <label key={org.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={inviteFormData.org_ids.includes(org.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setInviteFormData({
                                        ...inviteFormData,
                                        org_ids: [...inviteFormData.org_ids, org.id]
                                      });
                                    } else {
                                      setInviteFormData({
                                        ...inviteFormData,
                                        org_ids: inviteFormData.org_ids.filter(id => id !== org.id)
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-border text-primary"
                                />
                                <span className="text-sm text-foreground">{org.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {inviteFormData.org_ids.length} organization(s) selected
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button type="submit" className="px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium">
                          Send Invitation
                        </button>
                        <button type="button" onClick={resetInviteForm} className="px-6 py-3 bg-muted/50 text-foreground rounded-lg hover:bg-muted transition-all duration-300 font-medium">
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {developersList.length === 0 ? (
                    <div className="col-span-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center shadow-lg shadow-primary/5">
                      <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground text-lg">No developers found. Invite developers to get started.</p>
                    </div>
                  ) : (
                    developersList.map(dev => (
                    <div key={dev.id} className="group relative bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                              <Users className="w-6 h-6 text-primary-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg text-foreground">{dev.name}</h3>
                                {dev.is_super_admin && (
                                  <span className="px-2 py-0.5 bg-purple-500/10 text-purple-500 border border-purple-500/20 rounded-md text-xs font-medium">
                                    Super Admin
                                  </span>
                                )}
                                {!dev.is_active && (
                                  <span className="px-2 py-0.5 bg-gray-500/10 text-gray-500 border border-gray-500/20 rounded-md text-xs font-medium">
                                    Inactive
                                  </span>
                                )}
                                {dev.is_restricted && (
                                  <span className="px-2 py-0.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded-md text-xs font-medium">
                                    Restricted
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{dev.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditDeveloperModal(dev)}
                              className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-all duration-300"
                              title="Edit Developer"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleSendPasswordReset(dev)}
                              disabled={sendingResetDevId === dev.id}
                              className="p-2 hover:bg-blue-500/10 text-blue-500 rounded-lg transition-all duration-300 disabled:opacity-50"
                              title="Send Password Reset Email"
                            >
                              <KeyRound className={`w-4 h-4 ${sendingResetDevId === dev.id ? 'animate-pulse' : ''}`} />
                            </button>
                            <button
                              onClick={() => handleToggleAccess(dev)}
                              className={`p-2 rounded-lg transition-all duration-300 ${
                                dev.is_active
                                  ? 'hover:bg-warning/10 text-warning'
                                  : 'hover:bg-success/10 text-success'
                              }`}
                              title={dev.is_active ? 'Revoke Login Access' : 'Restore Login Access'}
                            >
                              {dev.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDeleteDeveloper(dev.id, dev.name)}
                              className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                              title="Delete Developer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mb-4">
                          <p className="text-xs text-muted-foreground mb-2">
                            Organizations: {dev.organizations.length}
                          </p>
                          {dev.organizations.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {dev.organizations.map(org => (
                                <span key={org.id} className="px-2 py-1 bg-primary/10 text-primary border border-primary/20 rounded-md text-xs font-medium">
                                  {org.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => openAssignOrgModal(dev)}
                          className="w-full px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm flex items-center justify-center gap-2 transition-all duration-300 font-medium"
                          title="Assign Organizations"
                        >
                          <Plus className="w-4 h-4" />
                          Assign Organizations
                        </button>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Clients Tab */}
            {activeTab === 'clients' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Clients</h2>
                    <p className="text-sm text-muted-foreground mt-1">Manage client accounts and organization assignments</p>
                  </div>
                  <button
                    onClick={() => setShowClientForm(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                  >
                    <Plus className="w-5 h-5" />
                    Create Client
                  </button>
                </div>

                {showClientForm && (
                  <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5 animate-slide-in-up">
                    <form onSubmit={handleClientSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-foreground">Name *</label>
                          <input
                            type="text"
                            value={clientFormData.name}
                            onChange={(e) => setClientFormData({ ...clientFormData, name: e.target.value })}
                            required
                            className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-foreground">Email *</label>
                          <input
                            type="email"
                            value={clientFormData.email}
                            onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                            required
                            className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Company</label>
                        <input
                          type="text"
                          value={clientFormData.company}
                          onChange={(e) => setClientFormData({ ...clientFormData, company: e.target.value })}
                          className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          placeholder="Acme Inc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Assign Organizations (Optional)</label>
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-border/50 rounded-lg p-3 bg-background/50 backdrop-blur-sm">
                          {organizations.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No organizations available</p>
                          ) : (
                            organizations.map(org => (
                              <label key={org.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={clientFormData.org_ids.includes(org.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setClientFormData({
                                        ...clientFormData,
                                        org_ids: [...clientFormData.org_ids, org.id]
                                      });
                                    } else {
                                      setClientFormData({
                                        ...clientFormData,
                                        org_ids: clientFormData.org_ids.filter(id => id !== org.id)
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-border text-primary"
                                />
                                <span className="text-sm text-foreground">{org.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {clientFormData.org_ids.length} organization(s) selected
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button type="submit" className="px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium">
                          {editingClient ? 'Update' : 'Create'}
                        </button>
                        <button type="button" onClick={resetClientForm} className="px-6 py-3 bg-muted/50 text-foreground rounded-lg hover:bg-muted transition-all duration-300 font-medium">
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {clients.length === 0 ? (
                    <div className="col-span-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center shadow-lg shadow-primary/5">
                      <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground text-lg">No clients found. Create one to get started.</p>
                    </div>
                  ) : (
                    clients.map(client => (
                    <div key={client.id} className="group relative bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                              <Briefcase className="w-6 h-6 text-primary-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg text-foreground">{client.name}</h3>
                                {!client.is_active && (
                                  <span className="px-2 py-0.5 bg-gray-500/10 text-gray-500 border border-gray-500/20 rounded-md text-xs font-medium">
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{client.email}</p>
                              {client.company && (
                                <p className="text-xs text-muted-foreground mt-1">{client.company}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingClient(client);
                                setClientFormData({
                                  name: client.name,
                                  email: client.email,
                                  company: client.company || '',
                                  org_ids: client.organizations.map(org => org.id)
                                });
                                setShowClientForm(true);
                              }}
                              className="p-2 hover:bg-muted/50 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClient(client.id, client.name)}
                              className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mb-4">
                          <p className="text-xs text-muted-foreground mb-2">
                            Organizations: {client.organizations.length}
                          </p>
                          {client.organizations.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {client.organizations.map(org => (
                                <span key={org.id} className="px-2 py-1 bg-primary/10 text-primary border border-primary/20 rounded-md text-xs font-medium">
                                  {org.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => openAssignOrgModalClient(client)}
                          className="w-full px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm flex items-center justify-center gap-2 transition-all duration-300 font-medium"
                          title="Assign Organizations"
                        >
                          <Plus className="w-4 h-4" />
                          Assign Organizations
                        </button>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Invitations Tab */}
            {activeTab === 'invitations' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Invitations</h2>
                    <p className="text-sm text-muted-foreground mt-1">View and manage developer invitations</p>
                  </div>
                  <button
                    onClick={loadData}
                    className="flex items-center gap-2 px-6 py-3 bg-muted/50 text-foreground rounded-xl hover:bg-muted transition-all duration-300 font-medium"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Refresh
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {invitations.length === 0 ? (
                    <div className="col-span-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center shadow-lg shadow-primary/5">
                      <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground text-lg">No invitations found.</p>
                    </div>
                  ) : (
                    invitations.map(inv => (
                    <div key={inv.id} className="group relative bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                              <Mail className="w-6 h-6 text-primary-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg text-foreground">{inv.email}</h3>
                              </div>
                              <div className="mb-2">
                                {getStatusBadge(inv.status)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p className="text-muted-foreground">
                            <span className="font-medium text-foreground">Invited by:</span> {inv.invited_by?.name || 'Unknown'}
                          </p>
                          <p className="text-muted-foreground">
                            <span className="font-medium text-foreground">Created:</span> {new Date(inv.created_at).toLocaleString()}
                          </p>
                          {inv.expires_at && (
                            <p className="text-muted-foreground">
                              <span className="font-medium text-foreground">Expires:</span> {new Date(inv.expires_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {inv.assigned_organizations && inv.assigned_organizations.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-border/50">
                            <p className="text-xs text-muted-foreground mb-2 font-medium">Assigned Organizations:</p>
                            <div className="flex flex-wrap gap-2">
                              {inv.assigned_organizations.map(org => (
                                <span key={org.id} className="px-2 py-1 bg-primary/10 text-primary border border-primary/20 rounded-md text-xs font-medium">
                                  {org.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Usage Tab */}
            {activeTab === 'usage' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-foreground mb-1">Usage</h1>
                    <p className="text-sm text-muted-foreground">Agent usage and cost tracking</p>
                  </div>
                </div>

                {/* Multi-Column Layout: Organization Selector & Period Selector */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Organization Selector */}
                  <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
                    <div className="flex items-center gap-2 mb-4">
                      <Building className="w-5 h-5 text-primary" />
                      <span className="font-semibold text-foreground">Select Organization</span>
                    </div>
                    <select
                      value={selectedOrgForUsage || ''}
                      onChange={(e) => {
                        setSelectedOrgForUsage(e.target.value);
                        setTimeout(() => fetchUsageData(), 100);
                      }}
                      className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                    >
                      <option value="">Select an organization...</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Period Selector */}
                  <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="w-5 h-5 text-primary" />
                      <span className="font-semibold text-foreground">Time Period</span>
                    </div>
                    <div className="space-y-4">
                      <div className="flex gap-2 flex-wrap items-center">
                        {['day', 'week', 'month', 'last_month'].map((p) => (
                          <button
                            key={p}
                            onClick={() => {
                              setUsagePeriod(p);
                              setUseCustomRange(false);
                              setCustomStartDate('');
                              setCustomEndDate('');
                              if (selectedOrgForUsage) {
                                setTimeout(() => fetchUsageData(), 100);
                              }
                            }}
                            className={`px-6 py-3 rounded-xl transition-all duration-300 font-medium ${
                              usagePeriod === p && !useCustomRange
                                ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                                : 'bg-muted/50 text-foreground hover:bg-muted hover:shadow-md'
                            }`}
                          >
                            {p === 'day' ? 'Today' : p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'Last Month'}
                          </button>
                        ))}
                        <button
                          onClick={fetchUsageData}
                          disabled={!selectedOrgForUsage || loading}
                          className="ml-auto px-6 py-3 bg-muted/50 border border-border/50 rounded-xl hover:bg-muted transition-all duration-300 flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>
                      <div className="pt-2 border-t border-border/50">
                        <div className="space-y-2">
                          <span className="text-sm text-muted-foreground font-medium">Custom Range:</span>
                          <div className="relative">
                            <DateRangePicker
                              startDate={customStartDate}
                              endDate={customEndDate}
                              onChange={(startDate, endDate) => {
                                setCustomStartDate(startDate);
                                setCustomEndDate(endDate);
                                const hasCustomRange = !!(startDate && endDate);
                                setUseCustomRange(hasCustomRange);
                                if (hasCustomRange && selectedOrgForUsage) {
                                  setUsagePeriod('custom');
                                  setTimeout(() => fetchUsageData(), 100);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" text="Loading usage data..." />
                  </div>
                )}

                {/* Error State */}
                {error && !loading && (
                  <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 text-center">
                    <div className="flex items-center justify-center gap-2 text-destructive mb-4">
                      <AlertCircle className="w-5 h-5" />
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={fetchUsageData}
                      disabled={!selectedOrgForUsage}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className="w-4 h-4 inline mr-2" />
                      Retry
                    </button>
                  </div>
                )}

                {/* No Organization Selected */}
                {!selectedOrgForUsage && !loading && !error && (
                  <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-8 text-center">
                    <Building className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Please select an organization to view usage data</p>
                  </div>
                )}

                {/* Usage Data */}
                {usageData && !loading && !error && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="group relative overflow-hidden bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 hover:border-primary/50">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                            <Clock className="w-6 h-6 text-primary-foreground" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-foreground">{usageData.total_minutes.toFixed(2)}</div>
                            <div className="text-xs text-muted-foreground">Total Minutes</div>
                          </div>
                        </div>
                      </div>
                      <div className="group relative overflow-hidden bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 hover:border-primary/50">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                            <DollarSign className="w-6 h-6 text-primary-foreground" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-foreground">${usageData.total_cost_usd.toFixed(4)}</div>
                            <div className="text-xs text-muted-foreground">Total Cost</div>
                          </div>
                        </div>
                      </div>
                      <div className="group relative overflow-hidden bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 hover:border-primary/50">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 rounded-xl bg-gradient-button flex items-center justify-center shadow-md">
                            <Bot className="w-6 h-6 text-primary-foreground" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-foreground">{usageData.agents.length}</div>
                            <div className="text-xs text-muted-foreground">Active Agents</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Period Info */}
                    <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4">
                      <div className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {usagePeriod === 'day' ? 'Today' : usagePeriod === 'week' ? 'This Week' : usagePeriod === 'month' ? 'This Month' : usagePeriod === 'last_month' ? 'Last Month' : 'Custom Range'}:
                        </span>{' '}
                        {new Date(usageData.start_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })} - {new Date(usageData.end_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    {/* Agent Usage Table */}
                    <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl overflow-hidden">
                      <div className="p-4 border-b border-border">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-primary" />
                          <h2 className="text-lg font-semibold text-foreground">Usage by Agent</h2>
                        </div>
                      </div>
                      {usageData.agents.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                          No usage data available for {usagePeriod === 'day' ? 'today' : usagePeriod === 'week' ? 'this week' : usagePeriod === 'month' ? 'this month' : usagePeriod === 'last_month' ? 'last month' : 'the selected period'}
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border bg-muted/30">
                                <th className="text-left p-4 text-sm font-medium text-foreground">Agent</th>
                                <th className="text-right p-4 text-sm font-medium text-foreground">Minutes</th>
                                <th className="text-right p-4 text-sm font-medium text-foreground">Cost</th>
                                <th className="text-right p-4 text-sm font-medium text-foreground">Sessions</th>
                                <th className="text-right p-4 text-sm font-medium text-foreground">Avg Cost/Min</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usageData.agents.map((agent, index) => {
                                const avgCostPerMin = agent.total_minutes > 0 
                                  ? agent.total_cost_usd / agent.total_minutes 
                                  : 0;
                                
                                return (
                                  <tr
                                    key={agent.agent_id}
                                    className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${
                                      index % 2 === 0 ? 'bg-card/30' : ''
                                    }`}
                                  >
                                    <td className="p-4">
                                      <div className="flex items-center gap-2">
                                        <Bot className="w-4 h-4 text-primary" />
                                        <span className="font-medium text-foreground">{agent.agent_name}</span>
                                      </div>
                                    </td>
                                    <td className="p-4 text-right">
                                      <div className="flex items-center justify-end gap-2">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground font-medium">
                                          {agent.total_minutes.toFixed(2)}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="p-4 text-right">
                                      <div className="flex items-center justify-end gap-2">
                                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground font-medium">
                                          ${agent.total_cost_usd.toFixed(4)}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="p-4 text-right">
                                      <span className="text-foreground">{agent.session_count}</span>
                                    </td>
                                    <td className="p-4 text-right">
                                      <div className="flex items-center justify-end gap-2">
                                        <TrendingUp className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">
                                          ${avgCostPerMin.toFixed(4)}
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Assign Organization Modal */}
        {showAssignOrgModal && selectedDeveloper && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl shadow-primary/10 animate-slide-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-button flex items-center justify-center">
                  <Building className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    Assign Organizations
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedDeveloper.name}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Select organizations this developer can access
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto border border-border/50 rounded-xl p-3 bg-background/50 backdrop-blur-sm mb-4">
                {organizations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No organizations available</p>
                ) : (
                  organizations.map(org => (
                    <label key={org.id} className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedOrgIds.includes(org.id)}
                        onChange={() => handleOrgSelection(org.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/50"
                      />
                      <span className="text-sm text-foreground font-medium">{org.name}</span>
                    </label>
                  ))
                )}
              </div>
              
              <div className="flex items-center justify-between mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-foreground">
                  {selectedOrgIds.length} organization(s) selected
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleAssignOrg(selectedDeveloper.id, selectedOrgIds)}
                  className="flex-1 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedOrgIds.length === 0}
                >
                  Assign Organizations
                </button>
                <button
                  onClick={() => {
                    setShowAssignOrgModal(false);
                    setSelectedDeveloper(null);
                    setSelectedOrgIds([]);
                  }}
                  className="px-6 py-3 bg-muted/50 text-foreground rounded-xl hover:bg-muted transition-all duration-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Organization Modal for Clients */}
        {showAssignOrgModalClient && selectedClient && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl shadow-primary/10 animate-slide-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-button flex items-center justify-center">
                  <Building className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    Assign Organizations
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedClient.name}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Select organizations for this client
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto border border-border/50 rounded-xl p-3 bg-background/50 backdrop-blur-sm mb-4">
                {organizations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No organizations available</p>
                ) : (
                  organizations.map(org => (
                    <label key={org.id} className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedOrgIdsClient.includes(org.id)}
                        onChange={() => handleOrgSelectionClient(org.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/50"
                      />
                      <span className="text-sm text-foreground font-medium">{org.name}</span>
                    </label>
                  ))
                )}
              </div>
              
              <div className="flex items-center justify-between mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-foreground">
                  {selectedOrgIdsClient.length} organization(s) selected
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleAssignOrgClient(selectedClient.id, selectedOrgIdsClient)}
                  className="flex-1 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedOrgIdsClient.length === 0}
                >
                  Assign Organizations
                </button>
                <button
                  onClick={() => {
                    setShowAssignOrgModalClient(false);
                    setSelectedClient(null);
                    setSelectedOrgIdsClient([]);
                  }}
                  className="px-6 py-3 bg-muted/50 text-foreground rounded-xl hover:bg-muted transition-all duration-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Developer Modal */}
        {showEditDeveloperModal && editingDeveloper && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl shadow-primary/10 animate-slide-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-button flex items-center justify-center">
                  <Edit2 className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    Edit Developer
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {editingDeveloper.name}
                  </p>
                </div>
              </div>

              <form onSubmit={handleEditDeveloperSubmit} className="space-y-4">
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors border border-border/50">
                    <input
                      type="checkbox"
                      checked={developerEditData.is_super_admin}
                      onChange={(e) => setDeveloperEditData({ ...developerEditData, is_super_admin: e.target.checked })}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">Super Admin</span>
                      <p className="text-xs text-muted-foreground">Grant super admin privileges</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors border border-border/50">
                    <input
                      type="checkbox"
                      checked={developerEditData.is_restricted}
                      onChange={(e) => setDeveloperEditData({ ...developerEditData, is_restricted: e.target.checked })}
                      className="w-4 h-4 rounded border-border text-red-500 focus:ring-2 focus:ring-red-500/50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">Restricted</span>
                      <p className="text-xs text-muted-foreground">Prevent developer from logging in</p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={resetEditDeveloperForm}
                    className="px-6 py-3 bg-muted/50 text-foreground rounded-xl hover:bg-muted transition-all duration-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">Profile</h1>
              <p className="text-sm text-muted-foreground">Manage your own account settings</p>
            </div>

            <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Account
              </h2>
              <div className="space-y-4">
                <div className="p-4 bg-background/50 rounded-lg border border-border/50">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Name</div>
                  <div className="text-lg font-semibold text-foreground">{myProfile?.name || 'N/A'}</div>
                </div>
                <div className="p-4 bg-background/50 rounded-lg border border-border/50">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Email</div>
                  <div className="text-lg font-semibold text-foreground">{myProfile?.email || 'N/A'}</div>
                </div>
                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Role</div>
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-500 border border-purple-500/30 rounded-md text-sm font-medium">
                    Super Admin
                  </span>
                </div>
              </div>
            </div>

            <ProfilePhoneSettings
              initialPhone={myProfile?.phone_number || null}
              initialSmsAvailable={Boolean(myProfile?.sms_2fa_available)}
              onSaved={(data) => setMyProfile((prev) => (prev ? { ...prev, ...data } : prev))}
            />
          </div>
        )}

        {/* IP Based Blocking Tab (self-loading) */}
        {activeTab === 'ip-blocking' && <IPBlockingPanel />}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-1">API Keys</h1>
                <p className="text-sm text-muted-foreground">Manage API keys for programmatic access</p>
              </div>
              <button
                onClick={() => setShowApiKeyForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
              >
                <Plus className="w-4 h-4" />
                Create API Key
              </button>
            </div>

            {/* API Keys List */}
            {loading ? (
              <LoadingSpinner />
            ) : apiKeysList.length === 0 ? (
              <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center">
                <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No API Keys</h3>
                <p className="text-muted-foreground mb-4">Create your first API key to enable programmatic access</p>
                <button
                  onClick={() => setShowApiKeyForm(true)}
                  className="px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                >
                  Create API Key
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {apiKeysList.map((key) => (
                  <div
                    key={key.id}
                    className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Key className="w-5 h-5 text-primary" />
                          <h3 className="text-lg font-semibold text-foreground">{key.key_name}</h3>
                          {key.is_active ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-500 rounded-lg text-xs font-medium">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-destructive/20 text-destructive rounded-lg text-xs font-medium">
                              Revoked
                            </span>
                          )}
                        </div>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Key:</span>
                            <code className="px-2 py-1 bg-background/50 rounded text-foreground font-mono">
                              {key.key_prefix}
                            </code>
                            <button
                              onClick={() => copyToClipboard(key.key_prefix, key.id)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="Copy key prefix"
                            >
                              <Copy className={`w-4 h-4 ${copiedKeyId === key.id ? 'text-primary' : ''}`} />
                            </button>
                          </div>
                          {key.scopes && key.scopes.length > 0 && (
                            <div>
                              <span className="font-medium">Scopes:</span> {key.scopes.join(', ')}
                            </div>
                          )}
                          {key.expires_at && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>
                            </div>
                          )}
                          {key.last_used_at && (
                            <div className="flex items-center gap-2">
                              <Activity className="w-4 h-4" />
                              <span>Last used: {new Date(key.last_used_at).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>Created: {new Date(key.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {key.is_active && (
                          <button
                            onClick={() => handleRevokeApiKey(key.id)}
                            className="px-4 py-2 bg-yellow-500/20 text-yellow-500 rounded-lg hover:bg-yellow-500/30 transition-colors text-sm font-medium"
                          >
                            Revoke
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteApiKey(key.id, key.key_name)}
                          className="px-4 py-2 bg-destructive/20 text-destructive rounded-lg hover:bg-destructive/30 transition-colors text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create API Key Form Modal */}
        {showApiKeyForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl shadow-primary/10 animate-slide-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Key className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">Create API Key</h3>
                  <p className="text-sm text-muted-foreground">Generate a new API key for programmatic access</p>
                </div>
              </div>

              <form onSubmit={handleCreateApiKey} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Key Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={apiKeyFormData.key_name}
                    onChange={(e) => setApiKeyFormData({ ...apiKeyFormData, key_name: e.target.value })}
                    placeholder="e.g., n8n Integration, Production API"
                    required
                    className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Expiration Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={apiKeyFormData.expires_at || ''}
                    onChange={(e) => setApiKeyFormData({ ...apiKeyFormData, expires_at: e.target.value || null })}
                    className="w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                  >
                    Create API Key
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowApiKeyForm(false);
                      setApiKeyFormData({ key_name: '', scopes: null, expires_at: null });
                    }}
                    className="px-6 py-3 bg-muted/50 text-foreground rounded-xl hover:bg-muted transition-all duration-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* New API Key Display Modal */}
        {showKeyModal && newlyCreatedKey && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl p-6 max-w-lg w-full shadow-2xl shadow-primary/10 animate-slide-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">API Key Created</h3>
                  <p className="text-sm text-muted-foreground">Copy your API key now. You won't be able to see it again!</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">API Key</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newlyCreatedKey.api_key}
                      readOnly
                      className="flex-1 px-4 py-3 bg-background/50 border border-border/50 rounded-lg font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(newlyCreatedKey.api_key, 'new')}
                      className="px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                      title="Copy API key"
                    >
                      {copiedKeyId === 'new' ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div className="text-sm text-yellow-500">
                      <strong>Important:</strong> Store this API key securely. You won't be able to view it again after closing this dialog.
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowKeyModal(false);
                      setNewlyCreatedKey(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
                  >
                    I've Saved It
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete / Access Confirmation Modal */}
        {showDeleteModal && (() => {
          const isRevoke = deleteConfirmation.type === 'revoke-access';
          const isRestore = deleteConfirmation.type === 'restore-access';
          const isAccessToggle = isRevoke || isRestore;

          return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl shadow-primary/10 animate-slide-in-up">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isRevoke
                      ? 'bg-warning/20'
                      : isRestore
                        ? 'bg-success/20'
                        : 'bg-destructive/20'
                  }`}>
                    {isRevoke
                      ? <ShieldOff className="w-5 h-5 text-warning" />
                      : isRestore
                        ? <ShieldCheck className="w-5 h-5 text-success" />
                        : <AlertTriangle className="w-5 h-5 text-destructive" />
                    }
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">
                      {isRevoke
                        ? 'Revoke Login Access'
                        : isRestore
                          ? 'Restore Login Access'
                          : 'Confirm Deletion'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isRevoke
                        ? 'Developer will be immediately signed out'
                        : isRestore
                          ? 'Developer will be able to log in again'
                          : 'This action cannot be undone'}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-foreground">
                    {isRevoke ? (
                      <>Are you sure you want to <span className="font-semibold text-warning">revoke login access</span> for <span className="font-semibold">"{deleteConfirmation.name}"</span>?</>
                    ) : isRestore ? (
                      <>Are you sure you want to <span className="font-semibold text-success">restore login access</span> for <span className="font-semibold">"{deleteConfirmation.name}"</span>?</>
                    ) : deleteConfirmation.type === 'org-assignment' ? (
                      <>Are you sure you want to remove the organization assignment for <span className="font-semibold">"{deleteConfirmation.name}"</span>?</>
                    ) : (
                      <>Are you sure you want to delete <span className="font-semibold">"{deleteConfirmation.name}"</span>?</>
                    )}
                  </p>
                  {isRevoke && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Their active sessions will be terminated immediately. You can restore access at any time.
                    </p>
                  )}
                  {!isAccessToggle && deleteConfirmation.type !== 'org-assignment' && (
                    <p className="text-sm text-muted-foreground mt-2">
                      This will permanently delete all associated data.
                    </p>
                  )}
                  {deleteConfirmation.type === 'organization' && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Its agents, phone numbers, call history and recordings go with it.
                      Phone numbers are released with the upstream provider so they stop billing.
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={confirmDelete}
                    className={`flex-1 px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-300 font-medium flex items-center justify-center gap-2 ${
                      isRevoke
                        ? 'bg-warning text-warning-foreground hover:bg-warning/90 hover:shadow-warning/20'
                        : isRestore
                          ? 'bg-success text-success-foreground hover:bg-success/90 hover:shadow-success/20'
                          : 'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-destructive/20'
                    }`}
                  >
                    {isRevoke
                      ? <><Lock className="w-4 h-4" /> Revoke Access</>
                      : isRestore
                        ? <><Unlock className="w-4 h-4" /> Restore Access</>
                        : 'Delete'}
                  </button>
                  <button
                    onClick={closeDeleteModal}
                    className="px-6 py-3 bg-muted/50 text-foreground rounded-xl hover:bg-muted transition-all duration-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
